import V4API, { getV4SiteUuid } from '../../api/V4client';
import { mapV4GeofenceToV3, v3GeofenceToV4Body } from './transforms';

// List + detail (members) so the cached record carries the link list for diffs.
export async function fetchV4Geofences(scope, accountId, siteUuidToV3Id) {
    try {
        const res = await V4API.geofencesList(scope);
        const items = res?.data || [];
        const detailed = await Promise.all(items.map(async (g) => {
            if (!g?.id) return g;
            try {
                const detail = await V4API.geofencesDetail(g.id, scope);
                return detail?.data || g;
            } catch (_) {
                return g;
            }
        }));
        return detailed.map((g) => mapV4GeofenceToV3(g, accountId, siteUuidToV3Id));
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch V4 geofences', e?.response?.data || e);
        return [];
    }
}

async function toV4GeofenceRequest(geofence) {
    const get = (k) => (geofence?.get ? geofence.get(k) : geofence?.[k]);
    const { coordinates, margin } = v3GeofenceToV4Body(geofence);
    const v3SiteId = +get('siteId') || 0;
    let site_id = null;
    if (v3SiteId > 0) {
        site_id = await getV4SiteUuid(v3SiteId);
        if (!site_id) throw new Error(`Site ${v3SiteId} is not available on V4`);
    }
    return {
        name: get('name'),
        description: get('description') || '',
        coordinates,
        margin,
        site_id,
    };
}

export async function saveV4Geofence(geofence, scope) {
    const body = await toV4GeofenceRequest(geofence);
    const id = geofence?.get ? geofence.get('id') : geofence?.id;
    const isNew = !id || id === 0;
    if (!isNew) {
        const res = await V4API.geofencesUpdate(id, body, scope);
        return res?.data;
    }
    const res = await V4API.geofencesCreate(body, scope);
    return res?.data;
}

export async function deleteV4Geofence(geofence, scope) {
    const id = geofence?.get ? geofence.get('id') : geofence?.id;
    if (!id) return;
    await V4API.geofencesDelete(id, scope);
}

export async function linkV4DeviceToGeofence(geofenceId, deviceUuid, scope) {
    if (!geofenceId || !deviceUuid) return;
    await V4API.geofencesDevicesCreate(geofenceId, deviceUuid, scope);
}

export async function unlinkV4DeviceFromGeofence(geofenceId, deviceUuid, scope) {
    if (!geofenceId || !deviceUuid) return;
    await V4API.geofencesDevicesDelete(geofenceId, deviceUuid, scope);
}

// Devices are referenced by V4 UUID — translate numeric V3 ids before calling.
export async function syncV4GeofenceDevices(geofenceId, nextDeviceUuids = [], prevDeviceUuids = [], scope) {
    if (!geofenceId) return;

    const nextSet = new Set((nextDeviceUuids || []).filter(Boolean).map(String));
    const prevSet = new Set((prevDeviceUuids || []).filter(Boolean).map(String));

    const toLink = [...nextSet].filter(id => !prevSet.has(id));
    const toUnlink = [...prevSet].filter(id => !nextSet.has(id));

    await Promise.all([
        ...toLink.map(uuid => linkV4DeviceToGeofence(geofenceId, uuid, scope)),
        ...toUnlink.map(uuid => unlinkV4DeviceFromGeofence(geofenceId, uuid, scope)),
    ]);
}

export function getV4GeofenceDeviceIds(geofence) {
    const members = geofence?.v4Data?.members
        || geofence?.get?.('v4Data')?.members
        || [];
    return members
        .filter(m => m && m.active !== false && m.device_id)
        .map(m => m.device_id);
}
